import { useState, useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Skeleton, Button } from "../components/ui"
import { Shell } from "../components/shell"
import { CardDialog } from "../components/card-dialog"
import { LabelBadge } from "../components/label-badge"
import { Calendar, CheckSquare, Paperclip, MessageSquare, ClipboardList, ChevronUp, ChevronDown, X } from "lucide-react"

const PRIORITY_COLORS: Record<string, string> = {
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
  medium: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
  low: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
}

type TaskCard = {
  id: string
  title: string
  description: string | null
  priority: string | null
  dueDate: string | null
  labels: string | null
  board: { id: string; name: string; labelColors: Record<string, string> | null; project: { id: string; name: string; workspace: { id: string; name: string } } }
  comments?: any[]
  attachments?: any[]
  checklistItems?: { id: string; checked: boolean }[]
}

/** My tasks page. Shows all cards assigned to the current user across all workspaces, sorted by due date. */
export default function MyTasksPage() {
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()
  const [openCard, setOpenCard] = useState<{ cardId: string; boardId: string; workspaceId: string } | null>(null)
  const [search, setSearch] = useState("")
  const [filterPriority, setFilterPriority] = useState("")
  const [filterBoard, setFilterBoard] = useState("")
  const [sortKey, setSortKey] = useState<"dueDate" | "priority" | "title">("dueDate")
  const [sortAsc, setSortAsc] = useState(true)

  const { data: tasks, isLoading } = useQuery({
    queryKey: ["my-tasks"],
    queryFn: () => fetcher<TaskCard[]>("/api/cards/my-tasks"),
    enabled: !!session,
  })

  if (!session) {
    navigate({ to: "/login" })
    return null
  }

  const now = new Date()

  const allBoards = useMemo(() => {
    if (!tasks) return []
    const seen = new Set<string>()
    return tasks.filter((t) => { if (seen.has(t.board.id)) return false; seen.add(t.board.id); return true }).map((t) => ({ id: t.board.id, name: t.board.name }))
  }, [tasks])

  const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 }

  const filtered = useMemo(() => {
    let list = tasks || []
    if (search) list = list.filter((t) => t.title.toLowerCase().includes(search.toLowerCase()))
    if (filterPriority) list = list.filter((t) => t.priority === filterPriority)
    if (filterBoard) list = list.filter((t) => t.board.id === filterBoard)
    return [...list].sort((a, b) => {
      let cmp = 0
      if (sortKey === "dueDate") {
        const da = a.dueDate ? new Date(a.dueDate).getTime() : Infinity
        const db = b.dueDate ? new Date(b.dueDate).getTime() : Infinity
        cmp = da - db
      } else if (sortKey === "priority") {
        cmp = (PRIORITY_ORDER[a.priority || ""] ?? 99) - (PRIORITY_ORDER[b.priority || ""] ?? 99)
      } else if (sortKey === "title") {
        cmp = a.title.localeCompare(b.title)
      }
      return sortAsc ? cmp : -cmp
    })
  }, [tasks, search, filterPriority, filterBoard, sortKey, sortAsc])

  const overdue = filtered.filter((t) => t.dueDate && new Date(t.dueDate) < now)
  const upcoming = filtered.filter((t) => !t.dueDate || new Date(t.dueDate) >= now)

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortAsc((v) => !v)
    else { setSortKey(key); setSortAsc(true) }
  }
  const SortIcon = ({ k }: { k: typeof sortKey }) =>
    sortKey === k ? (sortAsc ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : null

  const CardRow = ({ task }: { task: TaskCard }) => {
    const due = task.dueDate ? new Date(task.dueDate) : null
    const isOverdue = due && due < now
    const ws = task.board.project.workspace.id
    const brd = task.board.id

    return (
      <button
        onClick={() => setOpenCard({ cardId: task.id, boardId: brd, workspaceId: ws })}
        className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-muted/40"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{task.title}</p>
          <p className="text-xs text-muted-foreground">
            {task.board.project.workspace.name} / {task.board.project.name} / {task.board.name}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {task.labels && task.labels.split(",").map((l) => l.trim()).filter(Boolean).map((label, i) => (
            <LabelBadge key={label} label={label} color={task.board.labelColors?.[label]} index={i} />
          ))}
          {task.priority && (
            <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${PRIORITY_COLORS[task.priority] || ""}`}>
              {task.priority}
            </span>
          )}
          {due && (
            <span className={`flex items-center gap-1 text-xs ${isOverdue ? "text-red-500 dark:text-red-400" : "text-muted-foreground"}`}>
              <Calendar className="h-3 w-3" />
              {due.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          )}
          <div className="flex items-center gap-2 text-muted-foreground">
            {task.comments && task.comments.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs"><MessageSquare className="h-3 w-3" />{task.comments.length}</span>
            )}
            {task.attachments && task.attachments.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs"><Paperclip className="h-3 w-3" />{task.attachments.length}</span>
            )}
            {task.checklistItems && task.checklistItems.length > 0 && (
              <span className="flex items-center gap-0.5 text-xs">
                <CheckSquare className="h-3 w-3" />
                {task.checklistItems.filter((i) => i.checked).length}/{task.checklistItems.length}
              </span>
            )}
          </div>
        </div>
      </button>
    )
  }

  const hasFilters = !!search || !!filterPriority || !!filterBoard

  return (
    <Shell title="My Tasks" back={{ to: "/", label: "Workspaces" }}>
      <main className="mx-auto w-full max-w-4xl p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks..."
            className="h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none">
            <option value="">All priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          {allBoards.length > 1 && (
            <select value={filterBoard} onChange={(e) => setFilterBoard(e.target.value)} className="h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none">
              <option value="">All boards</option>
              {allBoards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <div className="flex items-center gap-1 rounded-md border border-border bg-background px-2">
            <span className="text-xs text-muted-foreground">Sort:</span>
            {(["dueDate", "priority", "title"] as const).map((k) => (
              <button key={k} onClick={() => toggleSort(k)} className={`flex items-center gap-0.5 rounded px-1.5 py-1 text-xs transition-colors ${sortKey === k ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                {k === "dueDate" ? "Due" : k === "priority" ? "Priority" : "Title"} <SortIcon k={k} />
              </button>
            ))}
          </div>
          {hasFilters && (
            <Button size="sm" variant="outline" onClick={() => { setSearch(""); setFilterPriority(""); setFilterBoard("") }}>
              <X className="mr-1 h-3 w-3" /> Clear
            </Button>
          )}
          <span className="ml-auto text-sm text-muted-foreground">{filtered.length} task{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
          </div>
        ) : !tasks || tasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted">
              <ClipboardList className="h-7 w-7 text-muted-foreground" />
            </div>
            <h3 className="mb-1 text-lg font-semibold">No tasks assigned to you</h3>
            <p className="text-sm text-muted-foreground">When someone assigns a card to you, it will appear here.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card p-12 text-center">
            <p className="text-sm text-muted-foreground">No tasks match your filters.</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => { setSearch(""); setFilterPriority(""); setFilterBoard("") }}>Clear filters</Button>
          </div>
        ) : (
          <div className="space-y-6">
            {overdue.length > 0 && (
              <div>
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-red-500 dark:text-red-400">
                  <Calendar className="h-4 w-4" /> Overdue ({overdue.length})
                </h2>
                <div className="overflow-hidden rounded-xl border border-red-200 bg-card shadow-sm dark:border-red-900/40">
                  <div className="divide-y divide-border">
                    {overdue.map((t) => <CardRow key={t.id} task={t} />)}
                  </div>
                </div>
              </div>
            )}
            <div>
              <h2 className="mb-3 text-sm font-semibold text-muted-foreground">
                All tasks ({upcoming.length})
              </h2>
              <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <div className="divide-y divide-border">
                  {upcoming.map((t) => <CardRow key={t.id} task={t} />)}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      {openCard && (
        <CardDialog
          open={!!openCard}
          onClose={() => setOpenCard(null)}
          cardId={openCard.cardId}
          boardId={openCard.boardId}
          workspaceId={openCard.workspaceId}
        />
      )}
    </Shell>
  )
}
